/**
 * Spark: answer privately, only mutual overlap ever shows.
 *
 * A pass is forever invisible; a one-sided yes stays secret until it is not one-sided. Tier
 * opt-ins follow the same rule, which is why a locked tier says nothing about whether the other
 * partner has opted in — there is no state on this screen for "they have, you have not".
 */
import { Chip } from '@/components/chip';
import { Empty } from '@/components/empty';
import { Panel } from '@/components/panel';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHead } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { api } from '@/api';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { usePoll } from '@/usePoll';
import { useStore } from '@/store';
import * as React from 'react';
import { View } from 'react-native';

type Tier = 'mild' | 'medium' | 'hot';

interface SparkState {
  tiers: { tier: Tier; active: boolean; optedIn: boolean }[];
  prompts: { id: string; tier: string; text: string; myAnswer: 'yes' | 'no' | null }[];
  matches: { id: string; tier: string; text: string }[];
}

const TIER_LABELS: Record<Tier, string> = { mild: 'Mild', medium: 'Medium', hot: 'Hot' };

export function SparkScreen() {
  const { me, refreshBadges, fail } = useStore();
  const [state, setState] = React.useState<SparkState | null>(null);
  const [tier, setTier] = React.useState<Tier>('mild');
  const [optinPrompt, setOptinPrompt] = React.useState<Exclude<Tier, 'mild'> | null>(null);

  const load = React.useCallback(async () => {
    setState(await api.get<SparkState>('/api/spark'));
  }, []);

  const { loaded, error, reload } = usePoll(load, 30_000);

  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await Promise.all([load(), refreshBadges()]);
    } catch (e) {
      fail(e);
    }
  };

  if (!loaded) return (
    <Panel id="spark">
      <PanelSkeleton rows={2} />
    </Panel>
  );
  if (error) return (
    <Panel id="spark">
      <LoadFailed onRetry={reload} reason={error} />
    </Panel>
  );

  if (!state) return <Panel id="spark">{null}</Panel>;

  const open = state.prompts.filter((p) => p.tier === tier && !p.myAnswer);

  return (
    <Panel id="spark">
      {state.matches.length ? (
        <Card>
          <CardContent>
            <CardHead>Matches — you both said yes</CardHead>
            {state.matches.map((m) => (
              <Text key={m.id} className="bg-muted rounded-md p-3">
                {m.text}
              </Text>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <View className="flex-row flex-wrap gap-2">
        {state.tiers.map((t) => (
          <Chip
            key={t.tier}
            label={t.active ? TIER_LABELS[t.tier] : `${TIER_LABELS[t.tier]} · locked`}
            selected={tier === t.tier && t.active}
            onPress={() => {
              if (t.active) {
                setTier(t.tier);
                setOptinPrompt(null);
              } else if (!t.optedIn && t.tier !== 'mild') {
                setOptinPrompt(t.tier);
              }
              // Opted in but not active: the partner has not (visibly). Nothing to show, and
              // nothing to say — saying it would disclose the one-sided opt-in.
            }}
          />
        ))}
      </View>

      {optinPrompt ? (
        <Card>
          <CardContent>
            {/* Not the string form of CardHead: interpolated children are an array, and the
                band's string branch is what turns a title into a heading. */}
            <CardHead>
              <Text variant="heading">Unlock {TIER_LABELS[optinPrompt]}?</Text>
            </CardHead>
            <CardDescription>
              Opting in is invisible to {me?.partnerName ?? 'your partner'}. The tier only opens if you BOTH opt in —
              nobody ever learns about a one-sided yes. That is the whole deal.
            </CardDescription>
            <View className="flex-row flex-wrap gap-2">
              <Button onPress={() => run(async () => (await api.post('/api/spark/optin', { tier: optinPrompt }), setOptinPrompt(null)))}>
                <Text>Quietly opt in</Text>
              </Button>
              <Button variant="ghost" flush={false} onPress={() => setOptinPrompt(null)}>
                <Text>Leave it</Text>
              </Button>
            </View>
          </CardContent>
        </Card>
      ) : null}

      {open.length ? (
        open.map((p) => (
          <Card key={p.id}>
            <CardContent>
              <Text variant="heading">{p.text}</Text>
              <View className="flex-row flex-wrap gap-2">
                <Button onPress={() => run(() => api.post('/api/spark/answers', { promptId: p.id, answer: 'yes' }))}>
                  <Text>Into it</Text>
                </Button>
                <Button variant="ghost" flush={false} onPress={() => run(() => api.post('/api/spark/answers', { promptId: p.id, answer: 'no' }))}>
                  <Text>Pass</Text>
                </Button>
              </View>
            </CardContent>
          </Card>
        ))
      ) : (
        <Empty mark="✦" title="Nothing unanswered here" body="Matches appear where you overlap. A pass is never shown to anyone, ever." />
      )}
    </Panel>
  );
}
